// Tiny in-app i18n: a locale context + a useT() hook that returns a function
// for resolving UI strings. Locale persisted in localStorage so the switch
// survives reloads.
//
// Product/commodity names are *not* part of these maps: they're resolved
// per-call via `localizeProductName` from translations.ts, which already
// has CCP-canonical zh names (and English fallback for ru, since CCP
// itself doesn't ship Russian translations for PI commodities).
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import type { Locale } from "./translations";

export type { Locale };

export const SUPPORTED_LOCALES: Locale[] = ["en", "zh", "ru"];

const STORAGE_KEY = "pio_locale";

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  zh: "简体中文",
  ru: "Русский",
};

interface UIStrings {
  // Header / auth
  appTitle: string;
  loggedInAs: string;
  logout: string;
  loginButton: string;
  loginIntro: string;
  loginAccessNote: string;
  loginRedirecting: string;
  authenticating: string;
  authFailed: string;
  backToLogin: string;
  // Form sections / labels
  planetsHeading: string;
  planetsTotalSummary: (n: number) => string;
  planetsHelp: string;
  addPlanet: string;
  remove: string;
  noSurveyedYields: string;
  surveyedCount: (n: number) => string;
  planetName: string;
  planetType: string;
  radius: string;
  km: string;
  // Credits
  creditBalance: string;
  creditsRefilling: (refillRate: number) => string;
  creditsRefillEta: (seconds: number) => string;
  creditsExhausted: string;
  insufficientCredits: (required: number, balance: number) => string;
  // Run parameters
  runParameters: string;
  tripsPerWeek: string;
  haulerSkillLevel: string;
  pocoTaxRate: string;
  interplanetIskPerM3: string;
  sellStrategy: string;
  commandCenterLevel: string;
  accountingLevel: string;
  haulerShip: string;
  linkLengthFactor: string;
  runOptimization: string;
  optimizing: string;
  // Job / status
  jobLabel: string;
  status: string;
  // Result panes
  summary: string;
  netProfitPerHr: string;
  grossExportPerHr: string;
  totalImportCostPerHr: string;
  margin: string;
  netExportVolumePerHr: string;
  netImportVolumePerHr: string;
  netProduction: string;
  product: string;
  perHour: string;
  factoriesPerPlanet: string;
  planet: string;
  tier: string;
  count: string;
  noFactoriesAllocated: string;
  perPlanetFlows: string;
  perPlanetFlowsHelp: string;
  jitaImports: string;
  jitaExportsNet: string;
  transfersIn: string;
  transfersOut: string;
  fromPlanet: string;
  toPlanet: string;
  none: string;
  flowSummaryPill: (imp: number, exp: number, inN: number, outN: number) => string;
}

const en: UIStrings = {
  appTitle: "Planetary Industry Optimizer",
  loggedInAs: "Logged in as",
  logout: "Logout",
  loginButton: "Login with EVE Online",
  loginIntro: "Planetary Industry Optimizer",
  loginAccessNote: "Access is restricted to alliance members.",
  loginRedirecting: "Redirecting...",
  authenticating: "Authenticating...",
  authFailed: "Authentication failed",
  backToLogin: "Back to login",
  planetsHeading: "Planets",
  planetsTotalSummary: (n) => `${n} total`,
  planetsHelp:
    "Surveyed P0 output is per extractor head per hour. Leave at 0 for pure-manufacturing planets.",
  addPlanet: "+ Add planet",
  remove: "Remove",
  noSurveyedYields: "no surveyed yields",
  surveyedCount: (n) => `${n} surveyed`,
  planetName: "Planet name",
  planetType: "Planet type",
  radius: "radius",
  km: "km",
  creditBalance: "Credit balance",
  creditsRefilling: (refillRate) => `Refilling at ${refillRate.toFixed(2)} credits/sec`,
  creditsRefillEta: (seconds) =>
    seconds < 60
      ? `${seconds.toFixed(0)} s until enough credits`
      : `${(seconds / 60).toFixed(1)} min until enough credits`,
  creditsExhausted: "Out of credits",
  insufficientCredits: (required, balance) =>
    `Need ${required.toFixed(0)} credits to submit (you have ${balance.toFixed(1)}).`,
  runParameters: "Run parameters",
  tripsPerWeek: "Trips per week to Jita",
  haulerSkillLevel: "Hauler skill level",
  pocoTaxRate: "POCO tax rate",
  interplanetIskPerM3: "Inter-planet shipping cost ISK / m³",
  sellStrategy: "Sell strategy",
  commandCenterLevel: "Command center level",
  accountingLevel: "Accounting level",
  haulerShip: "Hauler ship",
  linkLengthFactor: "Link length / facility (× radius)",
  runOptimization: "Run optimization",
  optimizing: "Optimizing…",
  jobLabel: "Job",
  status: "Status",
  summary: "Summary",
  netProfitPerHr: "Net profit / hr",
  grossExportPerHr: "Gross export / hr",
  totalImportCostPerHr: "Total import cost / hr",
  margin: "Margin",
  netExportVolumePerHr: "Net export volume / hr",
  netImportVolumePerHr: "Net import volume / hr",
  netProduction: "Net production",
  product: "Product",
  perHour: "Per hour",
  factoriesPerPlanet: "Factories per planet",
  planet: "Planet",
  tier: "Tier",
  count: "Count",
  noFactoriesAllocated: "No factories allocated.",
  perPlanetFlows: "Per-planet flows",
  perPlanetFlowsHelp:
    "For each planet: what comes in from Jita, what gets shipped to other planets in-system, what's received from other planets, and what's shipped out to Jita as net export.",
  jitaImports: "Jita imports",
  jitaExportsNet: "Jita exports (net)",
  transfersIn: "Transfers in (from other planets)",
  transfersOut: "Transfers out (to other planets)",
  fromPlanet: "From",
  toPlanet: "To",
  none: "None.",
  flowSummaryPill: (imp, exp, inN, outN) => `${imp} in · ${exp} out · ${inN}↘ · ${outN}↗`,
};

const zh: UIStrings = {
  appTitle: "行星工业优化器",
  loggedInAs: "登录身份",
  logout: "退出登录",
  loginButton: "通过 EVE Online 登录",
  loginIntro: "行星工业优化器",
  loginAccessNote: "仅联盟成员可访问。",
  loginRedirecting: "正在跳转……",
  authenticating: "正在验证……",
  authFailed: "验证失败",
  backToLogin: "返回登录",
  planetsHeading: "行星",
  planetsTotalSummary: (n) => `共 ${n} 颗`,
  planetsHelp: "调查 P0 产量按每个采集器头每小时计。纯加工行星请保留为 0。",
  addPlanet: "+ 添加行星",
  remove: "删除",
  noSurveyedYields: "无调查产量",
  surveyedCount: (n) => `已调查 ${n} 项`,
  planetName: "行星名称",
  planetType: "行星类型",
  radius: "半径",
  km: "km",
  creditBalance: "计算点数",
  creditsRefilling: (refillRate) => `以 ${refillRate.toFixed(2)} 计算点/秒恢复`,
  creditsRefillEta: (seconds) =>
    seconds < 60
      ? `还差 ${seconds.toFixed(0)} 秒打到提交阈值`
      : `还差 ${(seconds / 60).toFixed(1)} 分钟打到提交阈值`,
  creditsExhausted: "计算点耗尽",
  insufficientCredits: (required, balance) =>
    `提交需要 ${required.toFixed(0)} 计算点，当前仅 ${balance.toFixed(1)} 。`,
  runParameters: "运行参数",
  tripsPerWeek: "每周前往吉他次数",
  haulerSkillLevel: "运输航技能等级",
  pocoTaxRate: "海关税率",
  interplanetIskPerM3: "行星间运输成本 ISK / m³",
  sellStrategy: "销售策略",
  commandCenterLevel: "指挥中心等级",
  accountingLevel: "会计学等级",
  haulerShip: "运输舰",
  linkLengthFactor: "链路长度 / 设施(× 半径)",
  runOptimization: "运行优化",
  optimizing: "正在优化……",
  jobLabel: "任务",
  status: "状态",
  summary: "汇总",
  netProfitPerHr: "净利润 / 小时",
  grossExportPerHr: "出口总额 / 小时",
  totalImportCostPerHr: "进口总成本 / 小时",
  margin: "利润率",
  netExportVolumePerHr: "净出口体积 / 小时",
  netImportVolumePerHr: "净进口体积 / 小时",
  netProduction: "净产出",
  product: "产品",
  perHour: "每小时",
  factoriesPerPlanet: "每行星工厂数",
  planet: "行星",
  tier: "层级",
  count: "数量",
  noFactoriesAllocated: "未分配任何工厂。",
  perPlanetFlows: "各行星物流",
  perPlanetFlowsHelp:
    "针对每颗行星:从吉他导入的物料、行星间转运、从其它行星接收、以及作为净出口运往吉他的物料。",
  jitaImports: "吉他进口",
  jitaExportsNet: "吉他出口(净)",
  transfersIn: "转入(来自其他行星)",
  transfersOut: "转出(发往其他行星)",
  fromPlanet: "来源",
  toPlanet: "目的地",
  none: "无。",
  flowSummaryPill: (imp, exp, inN, outN) => `进 ${imp} · 出 ${exp} · ${inN}↘ · ${outN}↗`,
};

const ru: UIStrings = {
  appTitle: "Оптимизатор Планетарной Промышленности",
  loggedInAs: "Вход выполнен как",
  logout: "Выйти",
  loginButton: "Войти через EVE Online",
  loginIntro: "Оптимизатор Планетарной Промышленности",
  loginAccessNote: "Доступ только для членов альянса.",
  loginRedirecting: "Перенаправление…",
  authenticating: "Аутентификация…",
  authFailed: "Аутентификация не удалась",
  backToLogin: "Вернуться ко входу",
  planetsHeading: "Планеты",
  planetsTotalSummary: (n) => `всего ${n}`,
  planetsHelp:
    "Данные обзора P0 указываются на одну головку добытчика в час. Для чисто производственных планет оставьте 0.",
  addPlanet: "+ Добавить планету",
  remove: "Удалить",
  noSurveyedYields: "нет данных обзора",
  surveyedCount: (n) => `${n} обследовано`,
  planetName: "Название планеты",
  planetType: "Тип планеты",
  radius: "радиус",
  km: "км",
  creditBalance: "Баланс кредитов",
  creditsRefilling: (refillRate) =>
    `Пополняется со скоростью ${refillRate.toFixed(2)} кредит/сек`,
  creditsRefillEta: (seconds) =>
    seconds < 60
      ? `Ещё ${seconds.toFixed(0)} сек до порога отправки`
      : `Ещё ${(seconds / 60).toFixed(1)} мин до порога отправки`,
  creditsExhausted: "Кредиты исчерпаны",
  insufficientCredits: (required, balance) =>
    `Для отправки нужно ${required.toFixed(0)} кредитов, у вас ${balance.toFixed(1)}.`,
  runParameters: "Параметры запуска",
  tripsPerWeek: "Рейсов в Неделю до Джиты",
  haulerSkillLevel: "Уровень навыка транспортника",
  pocoTaxRate: "Налог POCO",
  interplanetIskPerM3: "Стоимость перевозки между планетами ISK / м³",
  sellStrategy: "Стратегия продаж",
  commandCenterLevel: "Уровень командного центра",
  accountingLevel: "Уровень Accounting",
  haulerShip: "Транспортный корабль",
  linkLengthFactor: "Длина линка / объект (× радиус)",
  runOptimization: "Запустить оптимизацию",
  optimizing: "Оптимизация…",
  jobLabel: "Задача",
  status: "Статус",
  summary: "Сводка",
  netProfitPerHr: "Чистая прибыль / час",
  grossExportPerHr: "Валовый экспорт / час",
  totalImportCostPerHr: "Общая стоимость импорта / час",
  margin: "Маржа",
  netExportVolumePerHr: "Объём экспорта / час",
  netImportVolumePerHr: "Объём импорта / час",
  netProduction: "Чистое производство",
  product: "Продукт",
  perHour: "В час",
  factoriesPerPlanet: "Заводы по планетам",
  planet: "Планета",
  tier: "Тир",
  count: "Кол-во",
  noFactoriesAllocated: "Заводы не размещены.",
  perPlanetFlows: "Потоки по планетам",
  perPlanetFlowsHelp:
    "Для каждой планеты: что приходит из Джиты, что уходит на другие планеты в системе, что приходит с других планет и что уходит на Джиту как чистый экспорт.",
  jitaImports: "Импорт с Джиты",
  jitaExportsNet: "Экспорт на Джиту (нетто)",
  transfersIn: "Поступления (с других планет)",
  transfersOut: "Отправления (на другие планеты)",
  fromPlanet: "Откуда",
  toPlanet: "Куда",
  none: "Нет.",
  flowSummaryPill: (imp, exp, inN, outN) => `вх ${imp} · исх ${exp} · ${inN}↘ · ${outN}↗`,
};

const STRINGS: Record<Locale, UIStrings> = { en, zh, ru };

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: UIStrings;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && (SUPPORTED_LOCALES as string[]).includes(stored)) {
    return stored as Locale;
  }
  return "en";
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage may be disabled (private mode); the in-memory state still works.
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, t: STRINGS[locale] }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used inside <LocaleProvider>");
  }
  return ctx;
}

export function useT(): UIStrings {
  return useLocale().t;
}
